/**
 * Typed API client. Token + active org persisted in localStorage.
 */

export interface AuthUser {
  id: string;
  email: string;
  name: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  widgetKey?: string;
}

export interface Customer {
  id: string;
  lastName: string;
  firstName?: string | null;
  email?: string | null;
  phone?: string | null;
  city?: string | null;
  state?: string | null;
}

export interface Lead {
  id: string;
  customerId: string;
  stage: string;
  source: string;
  score?: number | null;
  estimatedKwp?: number | null;
  estimatedValueEur?: number | null;
  probabilityPct?: number | null;
  notes?: string | null;
  widgetPayload?: Record<string, unknown> | null;
  createdAt?: string;
}

export interface PriceBookEntry {
  key: string;
  valueEur: number;
  note?: string | null;
}

export interface FollowUp {
  id: string;
  leadId: string;
  dueAt: string;
  kind: string;
  note?: string | null;
  doneAt?: string | null;
}

export interface Project {
  id: string;
  name: string;
  status: string;
  projectType: string;
  customerId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Site {
  id: string;
  addressLine?: string | null;
  city?: string | null;
  state?: string | null;
  lat: number;
  lon: number;
  annualConsumptionKwh?: number | null;
  loadProfile: string;
  electricityPriceCtKwh: number;
}

export interface SurfaceDto {
  id: string;
  name: string;
  kind: string;
  boundaryLocal: { vertices: { x: number; y: number }[] };
  obstacles: { center: { x: number; y: number }; radiusM: number; heightM: number; kind: string }[];
  azimuthDeg: number;
  tiltDeg: number;
  edgeSetbackM: number;
  obstacleBufferM: number;
  gridRotationDeg?: number | null;
}

export type SurfaceOverrides = Record<
  string,
  { removedModuleKeys?: string[]; movedModules?: Record<string, { dx: number; dy: number }> }
>;

export interface Design {
  id: string;
  version: number;
  name: string;
  moduleId: string;
  inverterId: string | null;
  inverterCount: number;
  moduleWp?: number | null;
  inverterAcKw?: number | null;
  removedModuleKeys?: string[];
  movedModules?: Record<string, { dx: number; dy: number }> | null;
  surfaceOverrides?: SurfaceOverrides | null;
  batteryId?: string | null;
  dispatchStrategy?: string;
  accessoryOverrides?: Record<string, number> | null;
  microinverterId?: string | null;
  optimizerId?: string | null;
  evChargerId?: string | null;
  heatPumpId?: string | null;
  emsId?: string | null;
  meterId?: string | null;
  origin?: "manual" | "ai";
  aiRationale?: string | null;
  layoutOptions: { orientation: "portrait" | "landscape"; moduleGapM: number; rowGapM: number };
}

export interface CatalogueModule {
  id: string;
  manufacturer: string;
  model: string;
  wp: number;
  widthM: number;
  heightM: number;
  efficiencyPct: number;
  cellTechnology: string;
  listPriceEur: number;
  warrantyProductYears: number;
  warrantyPerformanceYears: number;
  vocV: number;
  betaVocPctPerK: number;
  vmppV: number;
  imppA: number;
  iscA: number;
  gammaPmaxPctPerK: number;
  degradationPctPerYear: number;
  firstYearDegradationPct: number;
  bifacial: boolean;
  variants?: { wp: number; vocV: number; vmppV: number; imppA: number; iscA: number; efficiencyPct: number; priceEur: number }[];
}

export interface CatalogueInverter {
  id: string;
  manufacturer: string;
  model: string;
  acKw: number;
  hybrid: boolean;
  class: string;
  listPriceEur: number;
  compatibleBatteryIds: string[];
  variants?: { acKw: number; maxDcKw: number; priceEur: number; label: string }[];
}

export interface CatalogueBattery {
  id: string;
  manufacturer: string;
  model: string;
  usableKwh: number;
  acCoupled: boolean;
  integratedInverter?: { acKw: number; maxDcKw: number; mppt: unknown[] } | null;
  listPriceEur: number;
  warrantyYears: number;
}

export interface SimulationResponse {
  simulationId: string;
  weatherSource: string;
  attribution: string;
  disclaimer: string;
  result: {
    layout: { moduleCount: number; dcKwp: number; moduleAreaM2: number; avgShadeFactor: number };
    energy: {
      annualAcKwh: number;
      monthlyAcKwh: number[];
      specificYieldKwhPerKwp: number;
      performanceRatio: number;
      clippingLossKwh: number;
      temperatureLossPct: number;
      inverterLossPct: number;
    };
    balance: {
      annualProductionKwh: number;
      annualConsumptionKwh: number;
      selfConsumedKwh: number;
      gridFeedInKwh: number;
      selfConsumptionRate: number;
      selfSufficiencyRate: number;
    };
    strings: {
      modulesPerString: number;
      stringCount: number;
      vocColdV: number;
      dcAcRatio: number;
      valid: boolean;
      violations: string[];
      warnings: string[];
    };
    issues: { severity: string; code: string; message: string }[];
    cost: {
      positions: { label: string; category: string; amountEur: number }[];
      totalNetEur: number;
      totalGrossEur: number;
      vatRate: number;
      eurPerKwp: number;
    };
    finance: {
      investmentEur: number;
      paybackYears: number | null;
      npvEur: number;
      irrPct: number | null;
      lcoeEurPerKwh: number;
      breakEvenYear: number | null;
      annualBenefitYear1Eur: number;
      cashflow: { year: number; netCashflowEur: number; cumulativeEur: number }[];
    };
    regulatory: { notes: string[]; smartMeterRequired: boolean; vatRate: number };
    co2SavedTonsPerYear: number;
    perSurface?: { surfaceId: string; layout: { moduleCount: number; dcKwp: number } }[];
    dynamicTariff?: {
      provider: string;
      spotAvgCtPerKwh: number;
      negativeHourSharePct: number;
      feedInNegativeSharePct: number;
      consumerAvgCtPerKwh: number;
      importCostFlatEur: number;
      importCostDynamicEur: number;
      tariffSavingsEur: number;
      arbitrageGainEur: number;
      additionalAnnualBenefitEur: number;
    };
    inverterGroups?: {
      label: string;
      inverterModel: string;
      count: number;
      dcKwp: number;
      acKw: number;
      dcAcRatio: number;
      annualAcKwh: number;
      clippingLossKwh: number;
      strings: { valid: boolean; stringCount: number; modulesPerString: number };
    }[];
  };
}

export interface PermitTask {
  id: string;
  kind: string;
  status: string;
  notes?: string | null;
}

export interface Proposal {
  id: string;
  number: string;
  status: string;
  totalNetEur: number;
  totalGrossEur: number;
  validUntil?: string | null;
  sentAt?: string | null;
  signedAt?: string | null;
}

export interface DesignCandidate {
  rank: number;
  label: string;
  inverterId: string | null;
  inverterAcKw: number | null;
  inverterCount: number;
  batteryId: string | null;
  emsId: string | null;
  dispatchStrategy: "self_consumption" | "price_optimized";
  inverterName: string;
  batteryName: string | null;
  batteryKwh: number | null;
  moduleCount: number;
  dcKwp: number;
  dcAcRatio: number;
  annualAcKwh: number;
  specificYieldKwhPerKwp: number;
  selfSufficiencyPct: number;
  selfConsumptionPct: number;
  investmentEur: number;
  npvEur: number;
  irrPct: number | null;
  paybackYears: number | null;
  annualBenefitYear1Eur: number;
  stringsValid: boolean;
  meetsConstraints: boolean;
  rationale: string;
}

export interface AutoDesignResponse {
  evaluated: number;
  skipped: number;
  objective: string;
  alternatives: { title: string; rank: number; tradeOff: string }[];
  sensitivity: { electricityPriceChangePct: number; npvEur: number; paybackYears: number | null; annualBenefitYear1Eur: number }[];
  constraintViolations: { rank: number; reason: string }[];
  weatherSource: string;
  module: { id: string; wp: number | null; name: string };
  targetDcAcRatio: number;
  candidates: DesignCandidate[];
  createdDesign: Design | null;
}

export interface CatalogueEvCharger {
  id: string;
  manufacturer: string;
  model: string;
  acKw: number;
  phases: number;
  pvSurplusCapable: boolean;
  ecosystem: string;
  protocol: string;
  cable: string;
  listPriceEur: number;
}

export interface CatalogueHeatPump {
  id: string;
  manufacturer: string;
  model: string;
  thermalKw: number;
  scopW35: number;
  electricalKwMax: number;
  sgReady: boolean;
  refrigerant: string;
  listPriceEur: number;
}

export interface CatalogueEms {
  id: string;
  manufacturer: string;
  model: string;
  protocols: string[];
  controls: string[];
  compatibleInverterBrands: string[];
  listPriceEur: number;
}

export interface CatalogueMeter {
  id: string;
  manufacturer: string;
  model: string;
  phases: number;
  maxCurrentA: number;
  protocol: string;
  listPriceEur: number;
}

export interface CatalogueMicroinverter {
  id: string;
  manufacturer: string;
  model: string;
  unitAcVa: number;
  modulesPerUnit: number;
  phases: number;
  maxModuleWp: number;
  listPriceEur: number;
}

export interface CatalogueOptimizer {
  id: string;
  manufacturer: string;
  model: string;
  maxModuleWp: number;
  requiresInverterBrand: string | null;
  compatibleInverterBrands: string[];
  listPriceEur: number;
}

export interface MlpeOption {
  kind: "optimizer" | "microinverter";
  componentId: string;
  label: string;
  gainKwhYear: number;
  gainPct: number;
  addedInvestmentEur: number;
  npvDeltaEur: number;
  paybackDeltaYears: number | null;
  clippingLossKwh: number;
  selfSufficiencyDeltaPp: number;
  verdict: "recommended" | "not_worth_it" | "required" | "incompatible";
  reasons: string[];
}

export interface MlpeAdvice {
  baseline: { architecture: string; annualAcKwh: number; investmentEur: number; npvEur: number; paybackYears: number | null; clippingLossKwh: number };
  shadeDispersionPct: number;
  seriesMismatchLossPct: number;
  options: MlpeOption[];
  recommendation: { choice: "none" | "optimizer" | "microinverter"; componentId: string | null; explanation: string };
}

export interface IntegrationProvider {
  id: string;
  name: string;
  type: string;
  status: "active" | "locked" | "stub";
  costLabel: string;
  confidencePct: number;
  freshness: string;
  lockReason: string | null;
  capabilities?: string[];
  regions?: string[];
  resolution?: string;
  auth?: string;
  fallbackPriority?: number;
  minPlan?: string;
  settings?: { providerId: string; enabled: boolean; apiKeySet: boolean; preferred: boolean; autoApproveUnderEur: number };
}

export interface PaidDataRecommendation {
  providerId: string;
  providerName: string;
  estimatedCostEur: number | null;
  costModel: string;
  currentConfidencePct: number;
  expectedConfidencePct: number;
  why: string;
  necessity: string;
  requiresApproval: boolean;
}

export interface GeoProviderEval {
  providerId: string;
  name: string;
  source: string;
  status: "active" | "coverage_directory" | "commercial_stub";
  available: boolean;
  coverageNote: string;
  cost: { model: string; estimatedEurPerSite: number | null; licensingNote: string };
}

export interface ReconstructResponse {
  twinId: string | null;
  chosen: { providerId: string; source: string; confidence: { overall: number; method: string }; attribution: string; roofPlanes: unknown[] } | null;
  evaluated: GeoProviderEval[];
  decision: string;
  surfacesCreated: SurfaceDto[];
}

export interface Supplier {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  apiIntegration?: string | null;
}

export interface ProcurementOrder {
  id: string;
  projectId: string;
  supplierId?: string | null;
  status: string;
  orderedAt?: string | null;
  expectedAt?: string | null;
  deliveredAt?: string | null;
  totalNetEur?: number | null;
  items: { componentType: string; componentId: string; label: string; qty: number; unit: string; unitEur: number }[];
  notes?: string | null;
  createdAt: string;
}

export interface InstallationPlanDto {
  id: string;
  projectId: string;
  crewName?: string | null;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  notes?: string | null;
}

export interface InstallationTaskDto {
  id: string;
  planId: string;
  title: string;
  category: string;
  status: string;
  order: number;
  assignee?: string | null;
  /** E44: planned day, distributed from the plan window. */
  scheduledFor?: string | null;
  doneAt?: string | null;
}

export interface CommissioningDto {
  id: string;
  projectId: string;
  commissionedAt?: string | null;
  electricianName?: string | null;
  electricianRegistrationNo?: string | null;
  meterNumber?: string | null;
  mastrNumber?: string | null;
  measurements?: { insulationMOhm?: number; stringVocV?: number[]; stringIscA?: number[]; rcdTripMs?: number } | null;
}

export interface InstallationView {
  plan: InstallationPlanDto | null;
  tasks: InstallationTaskDto[];
  commissioning: CommissioningDto | null;
}

export interface AccessoryItemDto {
  sku: string;
  name: string;
  category: "mounting" | "dc" | "ac" | "monitoring";
  qty: number;
  unit: string;
  unitPriceEur: number;
  totalEur: number;
  rule: string;
  optional?: boolean;
}

export interface InstallationPackage {
  hardware: { kind: string; name: string; detail: string; qty: number; unitPriceEur: number; totalEur: number }[];
  hardwareTotalEur: number;
  accessories: { items: AccessoryItemDto[]; totalEur: number; byCategory: Record<string, number>; notes: string[] };
  grandTotalEur: number;
  moduleCount: number;
  dcKwp: number;
}

export interface ProjectDetail {
  project: Project;
  site: Site | null;
  surfaces: SurfaceDto[];
  designs: Design[];
  permitTasks: PermitTask[];
  proposals: Proposal[];
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

const LS_TOKEN = "pv_token";
const LS_ORG = "pv_org";

export function getToken(): string | null {
  try {
    return localStorage.getItem(LS_TOKEN);
  } catch {
    return null;
  }
}
export function getOrgId(): string | null {
  try {
    return localStorage.getItem(LS_ORG);
  } catch {
    return null;
  }
}
export function setAuth(token: string, orgId: string): void {
  localStorage.setItem(LS_TOKEN, token);
  localStorage.setItem(LS_ORG, orgId);
}
export function clearAuth(): void {
  localStorage.removeItem(LS_TOKEN);
  localStorage.removeItem(LS_ORG);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getToken();
  const res = await fetch(path, {
    method,
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(json?.error?.message ?? `HTTP ${res.status}`, res.status);
  }
  return json as T;
}

function orgPath(path: string): string {
  const org = getOrgId();
  if (!org) throw new ApiError("no active organization", 401);
  return `/v1/orgs/${org}${path}`;
}

export const api = {
  register: (data: { email: string; name: string; password: string; organizationName: string; inviteToken?: string }) =>
    request<{ token: string; user: AuthUser; organization: Organization }>("POST", "/v1/auth/register", data),
  /** E49 — validate an invite WITHOUT consuming it (prefill/lock + failure states). */
  inviteInfo: (token: string) =>
    request<{ state: "valid" | "used" | "expired" | "unknown"; email?: string | null; registrationMode?: string }>(
      "GET",
      `/v1/auth/invite-info?token=${encodeURIComponent(token)}`,
    ),
  /* E40 — account security */
  requestPasswordReset: (email: string) =>
    request<{ ok: boolean; message: string }>("POST", "/v1/auth/request-password-reset", { email }),
  resetPassword: (token: string, newPassword: string) =>
    request<{ ok: boolean }>("POST", "/v1/auth/reset-password", { token, newPassword }),
  verifyEmail: (token: string) =>
    request<{ ok: boolean }>("POST", "/v1/auth/verify-email", { token }),
  resendVerificationSelf: () =>
    request<{ ok: boolean }>("POST", "/v1/auth/resend-verification-self"),
  login: (data: { email: string; password: string }) =>
    request<{ token: string; user: AuthUser; organizations: { id: string; role: string }[] }>(
      "POST",
      "/v1/auth/login",
      data,
    ),
  logout: () => request<{ ok: boolean }>("POST", "/v1/auth/logout"),

  org: () =>
    request<Organization & { widgetKey: string; role: string; effectiveAccessMode: string; viewerEmailVerified: boolean }>(
      "GET",
      orgPath(""),
    ),

  integrations: {
    list: () => request<{ view: "member" | "admin"; plan: string; providers: IntegrationProvider[] }>("GET", orgPath("/integrations")),
    update: (providerId: string, data: { enabled?: boolean; apiKey?: string | null; preferred?: boolean; autoApproveUnderEur?: number }) =>
      request<{ providerId: string; enabled: boolean; apiKeySet: boolean }>("PATCH", orgPath(`/integrations/${providerId}`), data),
  },

  priceBook: {
    get: () => request<{ entries: PriceBookEntry[] }>("GET", orgPath("/pricebook")),
    put: (entries: { key: string; valueEur: number | null; note?: string }[]) =>
      request<{ entries: PriceBookEntry[] }>("PUT", orgPath("/pricebook"), { entries }),
  },

  components: () =>
    request<{ modules: CatalogueModule[]; inverters: CatalogueInverter[]; batteries: CatalogueBattery[]; evChargers: CatalogueEvCharger[]; heatPumps: CatalogueHeatPump[]; ems: CatalogueEms[]; meters: CatalogueMeter[]; microinverters: CatalogueMicroinverter[]; optimizers: CatalogueOptimizer[] }>(
      "GET",
      orgPath("/components"),
    ),

  customers: {
    list: () => request<Customer[]>("GET", orgPath("/customers")),
    create: (data: { lastName: string; firstName?: string; email?: string; city?: string; state?: string }) =>
      request<Customer>("POST", orgPath("/customers"), data),
  },
  leads: {
    list: () => request<Lead[]>("GET", orgPath("/leads")),
    create: (data: { customerId: string; source?: string; estimatedKwp?: number }) =>
      request<Lead>("POST", orgPath("/leads"), data),
    update: (id: string, data: Partial<Pick<Lead, "stage" | "score" | "probabilityPct">>) =>
      request<Lead>("PATCH", orgPath(`/leads/${id}`), data),
    detail: (id: string) =>
      request<{ lead: Lead; customer: Customer | null; followUps: FollowUp[] }>("GET", orgPath(`/leads/${id}`)),
    addFollowUp: (id: string, data: { dueAt: string; kind: string; note?: string }) =>
      request<FollowUp>("POST", orgPath(`/leads/${id}/followups`), data),
    setFollowUpDone: (id: string, followUpId: string, done: boolean) =>
      request<FollowUp>("PATCH", orgPath(`/leads/${id}/followups/${followUpId}`), { done }),
  },
  pipeline: () =>
    request<{ leads: Record<string, Lead[]>; projects: Record<string, Project[]> }>("GET", orgPath("/pipeline")),

  projects: {
    list: () => request<Project[]>("GET", orgPath("/projects")),
    create: (data: { name: string; customerId?: string; leadId?: string; projectType?: string }) =>
      request<Project>("POST", orgPath("/projects"), data),
    detail: (id: string) => request<ProjectDetail>("GET", orgPath(`/projects/${id}`)),
    update: (id: string, data: { name?: string; status?: string; notes?: string }) =>
      request<Project>("PATCH", orgPath(`/projects/${id}`), data),
    putSite: (id: string, data: Partial<Site> & { lat: number; lon: number }) =>
      request<Site>("PUT", orgPath(`/projects/${id}/site`), data),
    addSurface: (id: string, data: Omit<SurfaceDto, "id">) =>
      request<SurfaceDto>("POST", orgPath(`/projects/${id}/surfaces`), data),
    updateSurface: (id: string, surfaceId: string, data: Partial<Omit<SurfaceDto, "id">>) =>
      request<SurfaceDto>("PATCH", orgPath(`/projects/${id}/surfaces/${surfaceId}`), data),
    deleteSurface: (id: string, surfaceId: string) =>
      request<{ ok: boolean }>("DELETE", orgPath(`/projects/${id}/surfaces/${surfaceId}`)),
    suppliers: () => request<Supplier[]>("GET", orgPath("/suppliers")),
    createSupplier: (data: { name: string; email?: string; apiIntegration?: string }) =>
      request<Supplier>("POST", orgPath("/suppliers"), data),
    procurement: (id: string) => request<ProcurementOrder[]>("GET", orgPath(`/projects/${id}/procurement`)),
    createOrder: (id: string, data: { designId: string; supplierId?: string; notes?: string }) =>
      request<ProcurementOrder>("POST", orgPath(`/projects/${id}/procurement/from-package`), data),
    updateOrder: (id: string, orderId: string, data: { status?: string; supplierId?: string | null; notes?: string | null }) =>
      request<ProcurementOrder>("PATCH", orgPath(`/projects/${id}/procurement/${orderId}`), data),
    installation: (id: string) => request<InstallationView>("GET", orgPath(`/projects/${id}/installation`)),
    upsertInstallationPlan: (id: string, data: { crewName?: string | null; scheduledStart?: string | null; scheduledEnd?: string | null; notes?: string | null }) =>
      request<{ plan: InstallationPlanDto; tasks: InstallationTaskDto[] }>("POST", orgPath(`/projects/${id}/installation/plan`), data),
    updateInstallationTask: (id: string, taskId: string, data: { status?: string; assignee?: string | null }) =>
      request<InstallationTaskDto>("PATCH", orgPath(`/projects/${id}/installation/tasks/${taskId}`), data),
    saveCommissioning: (id: string, data: Record<string, unknown>) =>
      request<CommissioningDto>("PUT", orgPath(`/projects/${id}/installation/commissioning`), data),
    dataRecommendation: (id: string) =>
      request<{ recommendation: PaidDataRecommendation | null }>("GET", orgPath(`/projects/${id}/data-recommendation`)),
    approvePaidData: (id: string, providerId: string) =>
      request<{ status: string; note: string }>("POST", orgPath(`/projects/${id}/integrations/${providerId}/approve`), {}),
    twinProviders: (id: string) =>
      request<{ evaluated: GeoProviderEval[]; decision: string }>("GET", orgPath(`/projects/${id}/digital-twin/providers`)),
    twinReconstruct: (id: string, data: { apply?: boolean; preferredProviderId?: string } = {}) =>
      request<ReconstructResponse>("POST", orgPath(`/projects/${id}/digital-twin/reconstruct`), data),
    mlpeAdvice: (id: string, designId: string) =>
      request<MlpeAdvice>("POST", orgPath(`/projects/${id}/designs/${designId}/mlpe-advice`), {}),
    autodesign: (id: string, data: { apply?: boolean; applyRank?: number; moduleId?: string; moduleWp?: number; tariffProvider?: string; maxCandidates?: number; objective?: string; maxBudgetEur?: number; maxPaybackYears?: number; minAutarkyPct?: number; priceSensitivityPct?: number[] }) =>
      request<AutoDesignResponse>("POST", orgPath(`/projects/${id}/autodesign`), data),
    designPackage: (id: string, designId: string) =>
      request<InstallationPackage>("GET", orgPath(`/projects/${id}/designs/${designId}/package`)),
    /** E59 — server-decided basemap for the SiteCanvas (official DOP-WMS or Esri).
     *  E61 — optional preview coordinates: official imagery engages as soon as
     *  the location is known, not only after save+reload. */
    basemap: (id: string, coords?: { lat: number; lon: number; state?: string }) =>
      request<{
        active:
          | { kind: "wms"; providerId: string; endpoint: string; layer: string | null; format: string; maxRequestPx: number; groundResolutionM?: number; attribution: string; vintageNote?: string; fixedParams?: Record<string, string>; maxScaleDenominator?: number | null }
          | { kind: "esri"; providerId: string; attribution: string };
        bundesland: string | null;
      }>(
        "GET",
        orgPath(
          `/projects/${id}/basemap${coords ? `?lat=${coords.lat}&lon=${coords.lon}${coords.state ? `&state=${encodeURIComponent(coords.state)}` : ""}` : ""}`,
        ),
      ),
    updateDesign: (id: string, designId: string, data: { name?: string; accessoryOverrides?: Record<string, number> | null }) =>
      request<Design>("PATCH", orgPath(`/projects/${id}/designs/${designId}`), data),
    detectSurfaces: (id: string, provider: "auto" | "osm" | "google_solar", buildingId?: string) =>
      request<{
        provider: string;
        surfaces?: SurfaceDto[];
        /** E67: which better rungs did NOT answer, and why */
        sourceChain?: { source: string; outcome: string }[];
        /** E54: honest ambiguity — the planner picks instead of the code guessing */
        requiresSelection?: boolean;
        candidates?: { buildingId: string; distanceM: number; roofAreaM2: number; planeCount: number; outlineLocal: { x: number; y: number }[] }[];
      }>("POST", orgPath(`/projects/${id}/surfaces/detect`), { provider, ...(buildingId ? { buildingId } : {}) }),
    addDesign: (
      id: string,
      data: {
        name?: string;
        moduleId: string;
        inverterId?: string | null;
        inverterCount?: number;
        moduleWp?: number;
        inverterAcKw?: number;
        removedModuleKeys?: string[];
        surfaceOverrides?: SurfaceOverrides;
        inverterGroups?: { inverterId: string; acKw?: number; count: number; surfaceIds: string[]; label?: string }[];
        batteryId?: string | null;
        microinverterId?: string | null;
        optimizerId?: string | null;
        evChargerId?: string | null;
        heatPumpId?: string | null;
        emsId?: string | null;
        meterId?: string | null;
        dispatchStrategy?: "self_consumption" | "price_optimized";
        layoutOptions: Design["layoutOptions"];
        commissioningDate?: string;
      },
    ) => request<Design>("POST", orgPath(`/projects/${id}/designs`), data),
    simulate: (id: string, designId: string, data: { surfaceId?: string; weatherSource?: string; tariffProvider?: string } = {}) =>
      request<SimulationResponse>("POST", orgPath(`/projects/${id}/designs/${designId}/simulate`), data),
    generatePermits: (id: string) =>
      request<{ created: PermitTask[]; existing: PermitTask[] }>("POST", orgPath(`/projects/${id}/permits/generate`), {}),
    updatePermit: (id: string, permitId: string, status: string) =>
      request<PermitTask>("PATCH", orgPath(`/projects/${id}/permits/${permitId}`), { status }),
    createProposal: (id: string, designId: string) =>
      request<Proposal>("POST", orgPath(`/projects/${id}/proposals`), { designId }),
    sendProposal: (id: string, proposalId: string) =>
      request<{ proposal: Proposal; signUrl: string }>("POST", orgPath(`/projects/${id}/proposals/${proposalId}/send`), {}),
  },
};

/** Authenticated file download (Bearer header → blob → browser save).
 *  E47: on failure the API's own localized, claim-lock-compliant explanation is
 *  thrown (previously a generic "Download failed (HTTP n)" that discarded it). */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const token = getToken();
  const res = await fetch(orgPath(path), { headers: token ? { authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    let message = `Download fehlgeschlagen (HTTP ${res.status})`;
    try {
      const j = (await res.json()) as { error?: { message?: string } };
      if (j?.error?.message) message = j.error.message;
    } catch {
      /* non-JSON body → keep the generic line */
    }
    throw new ApiError(message, res.status);
  }
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** OpenStreetMap Nominatim geocoding (address → lat/lon). */
export async function geocode(
  q: string,
): Promise<{ lat: number; lon: number; label: string; state: string | null; city: string | null; postcode: string | null } | null> {
  try {
    // E69: addressdetails — the RESOLVED state must drive the Bundesland field
    // (Netzanmeldung/Netzbetreiber rules key on it), and the resolved postcode
    // is compared against the typed one to catch wrong-Marktplatz geocodes.
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&addressdetails=1&q=${encodeURIComponent(q)}`,
    );
    const j = (await res.json()) as {
      lat: string;
      lon: string;
      display_name: string;
      address?: { state?: string; city?: string; postcode?: string };
    }[];
    return j[0]
      ? {
          lat: +j[0].lat,
          lon: +j[0].lon,
          label: j[0].display_name,
          state: j[0].address?.state ?? null,
          city: j[0].address?.city ?? null,
          postcode: j[0].address?.postcode ?? null,
        }
      : null;
  } catch {
    return null;
  }
}
